import React, { useState } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { useToast } from "@chakra-ui/react";
import { FcGoogle } from "react-icons/fc";
import BrandLogo from "../Components/BrandLogo";
import { api, setSession, SIGNED_OUT_REASON_KEY } from "../lib/api";
import "./Login.css";

const Ar = () => (
  <svg className="ar" viewBox="0 0 14 14" fill="none">
    <path d="M1 7h12M9 3l4 4-4 4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

// Why the last login ended (set by lib/api endSession). Read once, then cleared.
const SIGNED_OUT_TEXT = {
  session_expired: "Your login expired. Logins last 7 days, so please sign in again.",
  session_revoked: "You were signed out because your password was changed or an admin ended your sessions. Sign in with your current password.",
  session_invalid: "Your login is no longer valid. Please sign in again.",
};

function takeSignedOutReason() {
  try {
    const code = sessionStorage.getItem(SIGNED_OUT_REASON_KEY);
    sessionStorage.removeItem(SIGNED_OUT_REASON_KEY);
    return SIGNED_OUT_TEXT[code] || null;
  } catch (e) {
    return null;
  }
}

export default function UserLogin() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [mfaToken, setMfaToken] = useState(null);
  const [mfaCode, setMfaCode] = useState("");
  const [forgotMode, setForgotMode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [notice, setNotice] = useState(takeSignedOutReason);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();
  const comingFrom =
    (location.state && location.state.from && location.state.from.pathname) || "/dashboard";

  async function handleSubmit(e) {
    if (e && e.preventDefault) e.preventDefault();
    if (!email || !password) return;
    setLoading(true);
    setError(null);
    try {
      const data = await api("/login", {
        method: "POST",
        body: JSON.stringify({ email, password }),
      });
      setNotice(null);
      if (data.mfaRequired) {
        setMfaToken(data.mfaToken);
      } else {
        setSession(data);
        toast({ title: "Welcome back", status: "success", duration: 1500 });
        navigate(comingFrom, { replace: true });
      }
    } catch (err) {
      // The server's message says what happened: wrong password, unknown
      // email, or too many attempts (with minutes left). Shown inline so it
      // stays readable.
      setError(err.message || "Login failed");
    } finally {
      setLoading(false);
    }
  }

  async function handleForgot() {
    if (!email) return;
    setLoading(true);
    try {
      const data = await api("/forgot-password", {
        method: "POST",
        body: JSON.stringify({ email }),
      });
      toast({
        title: data.message || "Your administrator has been notified.",
        status: "success",
        duration: 5000,
      });
      setForgotMode(false);
    } catch (err) {
      toast({ title: err.message || "Could not submit request", status: "error" });
    } finally {
      setLoading(false);
    }
  }

  async function handleMfaSubmit() {
    if (!mfaCode || mfaCode.length < 6) return;
    setLoading(true);
    setError(null);
    try {
      const data = await api("/login/mfa", {
        method: "POST",
        body: JSON.stringify({ mfaToken, code: mfaCode }),
      });
      setSession(data);
      toast({ title: "Welcome back", status: "success", duration: 1500 });
      navigate(comingFrom, { replace: true });
    } catch (err) {
      setError(err.message || "Invalid code");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="dl-root">
      {/* ────────────── LEFT — editorial panel ────────────── */}
      <aside className="dl-aside">
        <div className="dl-aside-top">
          <div className="dl-brand-group">
            <BrandLogo variant="lockup" size={40} mono />
            <span className="dl-app-tag">Attendance &amp; HR Portal</span>
          </div>
          <Link to="/" className="dl-back">← Back</Link>
        </div>

        <div className="dl-aside-mid dl-fade-in">
          <div className="dl-eyebrow">Attendance · HR · Access</div>
          <h2>
            Attendance &amp; HR,<br />
            <em>in one place.</em>
          </h2>
          <p>
            The Quantiv Media Attendance &amp; HR portal — check in, manage your leaves, WFH
            and your team. (Looking for the task manager? That's a different portal.)
          </p>
        </div>

        <div className="dl-aside-foot">
          <span>v. 1.0 — Mai 2026</span>
          <span>Designed in India</span>
        </div>
      </aside>

      {/* ────────────── RIGHT — sign-in form ────────────── */}
      <main className="dl-form-wrap">
        <form
          className="dl-form"
          onSubmit={(e) => { e.preventDefault(); forgotMode ? handleForgot() : mfaToken ? handleMfaSubmit() : handleSubmit(e); }}
        >
          {forgotMode ? (
            <>
              <div className="dl-form-eyebrow dl-fade-in">— Reset access</div>
              <h1 className="dl-fade-in d2">Forgot <em>password?</em></h1>
              <p className="dl-sub dl-fade-in d2">
                Enter your studio email. If it's registered, your administrator will be
                notified to reset your password and share a new one with you.
              </p>

              <div className="dl-field dl-fade-in d3">
                <label className="dl-label" htmlFor="forgot-email">Email address</label>
                <input
                  id="forgot-email"
                  className="dl-input"
                  type="email"
                  autoComplete="email"
                  placeholder="you@studio.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>

              <button type="submit" className="dl-cta dl-fade-in d4" disabled={loading}>
                {loading ? "Sending…" : "Notify my admin"} <Ar />
              </button>
              <button
                type="button"
                onClick={() => setForgotMode(false)}
                className="dl-google"
                style={{ marginTop: 8 }}
              >
                ← Back to sign in
              </button>
            </>
          ) : !mfaToken ? (
            <>
              <div className="dl-form-eyebrow dl-fade-in">— 01 / Sign In</div>
              <h1 className="dl-fade-in d2">
                Welcome <em>back.</em>
              </h1>
              <p className="dl-sub dl-fade-in d2">Use the email tied to your studio account.</p>

              {notice && !error && <div className="dl-notice" role="status">{notice}</div>}
              {error && <div className="dl-notice is-error" role="alert">{error}</div>}

              <div className="dl-field dl-fade-in d3">
                <label className="dl-label" htmlFor="email">Email address</label>
                <input
                  id="email"
                  className="dl-input"
                  type="email"
                  autoComplete="email"
                  placeholder="you@studio.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>

              <div className="dl-field dl-fade-in d3">
                <label className="dl-label" htmlFor="password">Password</label>
                <input
                  id="password"
                  className="dl-input"
                  type="password"
                  autoComplete="current-password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </div>

              <div className="dl-row dl-fade-in d4">
                <label className="dl-check">
                  <input type="checkbox" /> Remember me
                </label>
                <button
                  type="button"
                  className="dl-link"
                  onClick={() => setForgotMode(true)}
                  style={{ background: "none", border: "none", padding: 0, cursor: "pointer" }}
                >
                  Forgot password?
                </button>
              </div>

              <button type="submit" className="dl-cta dl-fade-in d4" disabled={loading}>
                {loading ? "Signing in…" : "Sign In"} <Ar />
              </button>

              <div className="dl-divider dl-fade-in d4">or</div>

              <a href="/auth/google" className="dl-google dl-fade-in d4">
                <FcGoogle size={18} /> Continue with Google
              </a>
            </>
          ) : (
            <>
              <div className="dl-form-eyebrow">— 02 / Two-factor</div>
              <h1>Verify <em>identity.</em></h1>
              <p className="dl-sub">Enter the 6-digit code from your authenticator app.</p>

              {error && <div className="dl-notice is-error" role="alert">{error}</div>}

              <div className="dl-pin">
                {[0, 1, 2, 3, 4, 5].map((i) => (
                  <input
                    key={i}
                    inputMode="numeric"
                    maxLength={1}
                    value={mfaCode[i] || ""}
                    onChange={(e) => {
                      const v = e.target.value.replace(/[^0-9]/g, "");
                      const arr = mfaCode.split("");
                      arr[i] = v;
                      const next = arr.join("").slice(0, 6);
                      setMfaCode(next);
                      if (v && i < 5) {
                        const nx = e.target.parentElement.children[i + 1];
                        if (nx) nx.focus();
                      }
                    }}
                  />
                ))}
              </div>

              <div className="dl-recovery">Or paste a recovery code</div>
              <input
                className="dl-input"
                placeholder="ABCD-EFGH-IJKL"
                value={mfaCode.length > 6 ? mfaCode : ""}
                onChange={(e) =>
                  setMfaCode(e.target.value.toUpperCase().replace(/[^A-Z0-9-]/g, ""))
                }
              />

              <button type="submit" className="dl-cta" disabled={loading}>
                {loading ? "Verifying…" : "Verify"} <Ar />
              </button>
              <button
                type="button"
                onClick={() => {
                  setMfaToken(null);
                  setMfaCode("");
                  setError(null);
                }}
                className="dl-google"
                style={{ marginTop: 8 }}
              >
                ← Back to sign in
              </button>
            </>
          )}
        </form>
      </main>
    </div>
  );
}
